import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getProject,
  addProject,
  updateProject,
  deleteProject,
} from "../../firebase/projectServices";
import { storage } from "../../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./Admin.css";
import SpaceParticles from "../../components/Particles/SpaceParticles";
import { FaSave, FaArrowLeft, FaImage, FaTimes, FaTrash } from "react-icons/fa";
import RichTextEditor from "../../components/Admin/RichTextEditor";

const ProjectEditor = () => {
  const { action, projectId } = useParams();
  const navigate = useNavigate();
  const isNewProject = action === "new";
  const isDeleteProject = action === "delete";

  const [project, setProject] = useState({
    title: "",
    description: "",
    type: "",
    demoLink: "",
    tags: [],
    image: "",
    featured: false,
  });

  const [loading, setLoading] = useState(!isNewProject);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [skipImageUpload, setSkipImageUpload] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      if (isNewProject) return;

      try {
        console.log("Fetching project with ID:", projectId);
        const projectData = await getProject(projectId);
        if (projectData) {
          setProject(projectData);
          setImagePreview(projectData.image || null);
        } else {
          setError("Projeto não encontrado.");
          navigate("/admin/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Falha ao carregar o projeto.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [isNewProject, projectId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProject((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (content) => {
    setProject((prev) => ({ ...prev, description: content }));
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() === "") return;

    if (!project.tags.includes(tagInput.trim())) {
      setProject((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
    }

    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove) => {
    setProject((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setSkipImageUpload(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setProject((prev) => ({ ...prev, image: "" }));
    setSkipImageUpload(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Upload image if there's a new one
      let imageUrl = project.image;

      if (imageFile && !skipImageUpload) {
        const storageRef = ref(
          storage,
          `project-images/${Date.now()}_${imageFile.name}`
        );
        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const projectData = {
        ...project,
        image: imageUrl,
      };

      if (isNewProject) {
        await addProject(projectData);
      } else {
        await updateProject(projectId, projectData);
      }

      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Error saving project:", err);
      setError(`Falha ao ${isNewProject ? "criar" : "atualizar"} o projeto.`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);

    try {
      await deleteProject(projectId);
      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Error deleting project:", err);
      setError("Falha ao excluir o projeto.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="admin-loading">
            <div className="loading-spinner"></div>
            <p>Carregando projeto...</p>
          </div>
        </div>
        <SpaceParticles />
      </div>
    );
  }

  if (isDeleteProject) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="admin-card">
            <h1 className="admin-title">Confirmar Exclusão</h1>
            <p className="delete-confirmation-text">
              Tem certeza que deseja excluir o projeto "{project.title}"? Esta
              ação não pode ser desfeita.
            </p>

            {error && <div className="admin-error">{error}</div>}

            <div className="admin-actions">
              <button
                className="admin-button admin-button-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? "Excluindo..." : "Sim, Excluir"}
              </button>
              <button
                className="admin-button admin-button-secondary"
                onClick={() => navigate("/admin/dashboard")}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
        <SpaceParticles />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-post-editor">
        <div className="admin-header">
          <h1 className="admin-title">
            {isNewProject ? "Novo Projeto" : "Editar Projeto"}
          </h1>
          <div className="admin-actions">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="admin-button admin-button-secondary"
            >
              <FaArrowLeft /> Voltar
            </button>
            <button
              onClick={handleSave}
              className="admin-button admin-button-primary"
              disabled={saving}
            >
              <FaSave /> {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}

        <form onSubmit={handleSave} className="post-editor-form">
          <div className="form-group">
            <label htmlFor="title">Título do Projeto:</label>
            <input
              type="text"
              id="title"
              name="title"
              value={project.title}
              onChange={handleChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Tipo de Projeto:</label>
            <input
              type="text"
              id="type"
              name="type"
              value={project.type}
              onChange={handleChange}
              className="form-control"
              placeholder="Ex: Plataforma Web, Aplicativo Mobile, etc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="demoLink">Link do Projeto:</label>
            <input
              type="url"
              id="demoLink"
              name="demoLink"
              value={project.demoLink}
              onChange={handleChange}
              className="form-control"
              placeholder="https://exemplo.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Descrição:</label>
            <RichTextEditor
              value={project.description}
              onChange={handleDescriptionChange}
              placeholder="Descreva o projeto..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="image">Imagem do Projeto:</label>
            <div className="image-upload-container">
              <input
                type="file"
                id="image"
                onChange={handleImageChange}
                accept="image/*"
                className="image-upload-input"
              />
              <label htmlFor="image" className="image-upload-label">
                <FaImage /> Selecionar Imagem
              </label>

              {imagePreview && (
                <div className="image-preview-container">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="image-preview"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="remove-image-button"
                  >
                    <FaTrash /> Remover
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Tags:</label>
            <div className="tag-input-container">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Adicionar tag"
                className="tag-input"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="tag-add-button"
              >
                Adicionar
              </button>
            </div>
            <div className="tag-list">
              {project.tags.map((tag, index) => (
                <div key={index} className="tag-item">
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="remove-tag-button"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={project.featured}
                onChange={(e) =>
                  setProject({ ...project, featured: e.target.checked })
                }
                className="checkbox-input"
              />
              <span className="checkbox-label">Destaque</span>
            </label>
            <small className="form-help-text">
              Marque para exibir este projeto com destaque no site
            </small>
          </div>
        </form>
      </div>
      <SpaceParticles />
    </div>
  );
};

export default ProjectEditor;
